import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Timer } from "lucide-react";
import compligaurdLogo from "@/assets/compligaurd-logo.webp";

const MFA_TIMEOUT_SECONDS = 120; // 2 minutes

interface MfaChallengeProps {
  factorId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function MfaChallenge({ factorId, onSuccess, onCancel }: MfaChallengeProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(MFA_TIMEOUT_SECONDS);
  const [expired, setExpired] = useState(false);

  // 2-minute countdown timer
  useEffect(() => {
    if (expired) return;
    if (timeLeft <= 0) {
      setExpired(true);
      setError("Verification timed out. Please sign in again.");
      return;
    }
    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, expired]);

  const handleTimeout = useCallback(async () => {
    await supabase.auth.signOut();
    onCancel();
  }, [onCancel]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError("Please enter a 6-digit code.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data: challenge, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyError) throw verifyError;

      onSuccess();
    } catch (err: any) {
      setError(err.message || "Invalid verification code. Please try again.");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src={compligaurdLogo} alt="CompliGUARD logo" className="h-8 w-8" />
          <span className="text-foreground text-xl font-bold tracking-wide">
            Compli<span className="gold-text">GUARD</span>
          </span>
        </div>

        <div className="bg-card p-8 rounded-sm border border-border shadow-[0_1px_4px_rgba(11,31,53,0.06)]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-bold text-foreground">Two-Factor Authentication</h1>
            </div>
            <div className={`flex items-center gap-1.5 text-sm font-mono ${timeLeft <= 30 ? "text-destructive" : "text-muted-foreground"}`}>
              <Timer className="h-4 w-4" />
              <span>{formatTime(timeLeft)}</span>
            </div>
          </div>

          <p className="text-text-body text-sm mb-6">
            Enter the 6-digit code from your authenticator app within 2 minutes.
          </p>

          {expired ? (
            <div className="space-y-4">
              <p className="text-destructive text-sm text-center">{error}</p>
              <Button
                type="button"
                onClick={handleTimeout}
                className="w-full h-11 font-semibold bg-primary text-primary-foreground hover:bg-accent-hover rounded-full"
              >
                Return to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/\D/g, ""));
                    setError("");
                  }}
                  placeholder="000000"
                  autoFocus
                  className="text-center text-2xl tracking-[0.4em] font-mono bg-card border-border text-foreground rounded-sm h-14"
                />
              </div>

              {error && (
                <p className="text-destructive text-sm text-center">{error}</p>
              )}

              <Button
                type="submit"
                loading={loading}
                disabled={code.length !== 6}
                className="w-full h-11 font-semibold bg-primary text-primary-foreground hover:bg-accent-hover rounded-full"
              >
                Verify
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                className="w-full text-text-caption hover:text-foreground text-sm"
              >
                Cancel & sign out
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
